import { Text } from '@eo-locale/react';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { RoutePath } from '../../config';
import { useContributions } from '../../hooks';
import { Button } from '../atoms';
import { UserApi } from '../../api';

export const MembershipStatus: FC = () => {
  const navigate = useNavigate();
  const { data: user } = useQuery('user', UserApi.fetch);

  const canPay = Boolean(user?.can_pay);
  const hasPending = Boolean(user?.meta?.has_pending_current_year);

  const contributions = useContributions();
  const list = Array.isArray(contributions) ? contributions : [];
  const isCheckoutButtonVisible = list.length > 0 && canPay && !hasPending;

  const fmt = useMemo(
    () => new Intl.DateTimeFormat('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' }),
    [],
  );

  const membership = user?.membership;

  const formatRange = (from?: string | null, to?: string | null) => {
    if (!from || !to) return null;
    const d1 = new Date(from);
    const d2 = new Date(to);
    if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
    return `${fmt.format(d1)} – ${fmt.format(d2)}`;
  };

  const currentRange = formatRange(membership?.valid_from, membership?.valid_to);
  const nextRange = formatRange(membership?.next_valid_from, membership?.next_valid_to);

  const { statusKey, statusClass } = useMemo(() => {
    if (hasPending || membership?.state === 'pending') {
      return { statusKey: 'membership:pending', statusClass: 'text-amber-900 font-semibold' };
    }

    if (membership?.state === 'active') {
      return { statusKey: 'membership:paid', statusClass: 'text-emerald-900 font-semibold' };
    }

    if (membership?.state === 'paid_future') {
      return { statusKey: 'membership:paid_future', statusClass: 'text-blue-900 font-semibold' };
    }

    return { statusKey: 'membership:unpaid', statusClass: 'text-rose-900 font-semibold' };
  }, [hasPending, membership?.state]);

  return (
    <div className="hidden md:flex items-center gap-2 mr-6">
      {isCheckoutButtonVisible && (
        <Button className="px-2.5" onClick={() => navigate(RoutePath.Checkout)}>
          <Text id="membership:make_payment" />
        </Button>
      )}

      <span className="block text-base leading-tight">
        <Text id="membership:status" />
        <span className={classNames('block', statusClass)}>
          <Text id={statusKey} />
        </span>

        {/* 1) jeśli aktywna teraz, pokaż bieżącą ważność */}
        {membership?.state === 'active' && currentRange && (
          <span className="block text-xs text-slate-700">
            <Text id="membership:validity" />: {currentRange}
          </span>
        )}

        {/* 2) jeśli opłacone na przyszłość (brak ważnej teraz) -> pokaż przyszły zakres jako główną informację */}
        {membership?.state === 'paid_future' && nextRange && (
          <span className="block text-xs text-slate-700">
            <Text id="membership:validity" />: {nextRange}
          </span>
        )}

        {/* 3) jeśli aktywne teraz, ale opłacone też na kolejny okres -> dopisz „Kolejny okres” */}
        {membership?.state === 'active' && nextRange && (
          <span className="block text-xs text-slate-700">
            <Text id="membership:next_validity" />: {nextRange}
          </span>
        )}
      </span>
    </div>
  );
};